<template>
  <v-dialog v-model="dialog" max-width="800" scrollable>
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        icon
        v-bind="attrs"
        v-on="on"
        :title="'Statystyki'"
        :style="`background: ${darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(30, 45, 42, 0.06)'};`"
      >
        <v-icon :color="darkMode ? '#60a5fa' : '#2563eb'">mdi-chart-line</v-icon>
      </v-btn>
    </template>

    <v-card :style="`background: ${darkMode ? '#111a24' : '#ffffff'};`">
      <v-card-title :style="`background: ${darkMode ? '#172433' : 'linear-gradient(115deg, #1b2727, #34413c)'}; color: #f8f4ed;`">
        <v-icon left :color="darkMode ? '#f59e0b' : '#d97706'">mdi-chart-box</v-icon>
        <span class="text-h6 font-weight-bold">Twoje Statystyki</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="dialog = false">
          <v-icon color="rgba(248, 244, 237, 0.7)">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4" :style="`color: ${darkMode ? '#f3f6f5' : '#172422'};`">
        <!-- Period Selector -->
        <v-btn-toggle v-model="selectedPeriod" mandatory class="mb-4" :dark="darkMode">
          <v-btn small value="7">7 dni</v-btn>
          <v-btn small value="30">30 dni</v-btn>
          <v-btn small value="90">90 dni</v-btn>
        </v-btn-toggle>

        <!-- Summary Cards -->
        <v-row class="mb-4">
          <v-col cols="6" sm="3">
            <v-card :style="`background: ${darkMode ? '#1a2636' : '#f7f9f7'}; border-left: 4px solid ${darkMode ? '#60a5fa' : '#2563eb'};`" elevation="0">
              <v-card-text class="pa-3">
                <div class="text-caption" :style="`color: ${darkMode ? '#9cb0aa' : '#536561'};`">Sesje</div>
                <div class="text-h5 font-weight-bold" :style="`color: ${darkMode ? '#f3f6f5' : '#172422'};`">{{ stats.totalSessions }}</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6" sm="3">
            <v-card :style="`background: ${darkMode ? '#1a2636' : '#f7f9f7'}; border-left: 4px solid ${darkMode ? '#34d399' : '#1d6e4e'};`" elevation="0">
              <v-card-text class="pa-3">
                <div class="text-caption" :style="`color: ${darkMode ? '#9cb0aa' : '#536561'};`">Dokładność</div>
                <div class="text-h5 font-weight-bold" :style="`color: ${darkMode ? '#f3f6f5' : '#172422'};`">{{ stats.averageAccuracy }}%</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6" sm="3">
            <v-card :style="`background: ${darkMode ? '#1a2636' : '#f7f9f7'}; border-left: 4px solid ${darkMode ? '#f59e0b' : '#d97706'};`" elevation="0">
              <v-card-text class="pa-3">
                <div class="text-caption" :style="`color: ${darkMode ? '#9cb0aa' : '#536561'};`">Najlepszy wynik</div>
                <div class="text-h5 font-weight-bold" :style="`color: ${darkMode ? '#f3f6f5' : '#172422'};`">{{ stats.bestScore }}</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6" sm="3">
            <v-card :style="`background: ${darkMode ? '#1a2636' : '#f7f9f7'}; border-left: 4px solid ${darkMode ? '#fb923c' : '#ea580c'};`" elevation="0">
              <v-card-text class="pa-3">
                <div class="text-caption" :style="`color: ${darkMode ? '#9cb0aa' : '#536561'};`">Seria</div>
                <div class="text-h5 font-weight-bold" :style="`color: ${darkMode ? '#f3f6f5' : '#172422'};`">{{ currentStreak }} 🔥</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Accuracy Chart -->
        <v-card :style="`background: ${darkMode ? '#1a2636' : '#f7f9f7'};`" elevation="0" class="mb-4">
          <v-card-title class="text-subtitle-1 font-weight-bold" :style="`color: ${darkMode ? '#f3f6f5' : '#172422'};`">
            Dokładność w czasie
          </v-card-title>
          <v-card-text>
            <div class="chart-container" style="height: 200px; position: relative;">
              <canvas ref="accuracyChart"></canvas>
            </div>
          </v-card-text>
        </v-card>

        <!-- Detailed Stats -->
        <v-row>
          <v-col cols="12" sm="6">
            <v-card :style="`background: ${darkMode ? '#1a2636' : '#f7f9f7'};`" elevation="0">
              <v-card-title class="text-subtitle-1 font-weight-bold" :style="`color: ${darkMode ? '#f3f6f5' : '#172422'};`">
                Szczegóły
              </v-card-title>
              <v-card-text>
                <div class="d-flex justify-space-between mb-2">
                  <span :style="`color: ${darkMode ? '#9cb0aa' : '#536561'};`">Pytania ogółem:</span>
                  <span class="font-weight-bold" :style="`color: ${darkMode ? '#f3f6f5' : '#172422'};`">{{ stats.totalQuestions }}</span>
                </div>
                <div class="d-flex justify-space-between mb-2">
                  <span :style="`color: ${darkMode ? '#9cb0aa' : '#536561'};`">Poprawne:</span>
                  <span class="font-weight-bold" :style="`color: ${darkMode ? '#34d399' : '#1d6e4e'};`">{{ stats.totalCorrect }}</span>
                </div>
                <div class="d-flex justify-space-between mb-2">
                  <span :style="`color: ${darkMode ? '#9cb0aa' : '#536561'};`">Błędne:</span>
                  <span class="font-weight-bold" :style="`color: ${darkMode ? '#f87171' : '#b83a2c'};`">{{ stats.totalWrong }}</span>
                </div>
                <div class="d-flex justify-space-between mb-2">
                  <span :style="`color: ${darkMode ? '#9cb0aa' : '#536561'};`">Średni wynik:</span>
                  <span class="font-weight-bold" :style="`color: ${darkMode ? '#f3f6f5' : '#172422'};`">{{ stats.averageScore }}</span>
                </div>
                <div class="d-flex justify-space-between">
                  <span :style="`color: ${darkMode ? '#9cb0aa' : '#536561'};`">Najlepsza seria:</span>
                  <span class="font-weight-bold" :style="`color: ${darkMode ? '#f3f6f5' : '#172422'};`">{{ stats.bestStreak }}</span>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6">
            <v-card :style="`background: ${darkMode ? '#1a2636' : '#f7f9f7'};`" elevation="0">
              <v-card-title class="text-subtitle-1 font-weight-bold" :style="`color: ${darkMode ? '#f3f6f5' : '#172422'};`">
                Zestawy
              </v-card-title>
              <v-card-text>
                <div v-if="!stats.setsPlayed || stats.setsPlayed.length === 0" :style="`color: ${darkMode ? '#9cb0aa' : '#536561'};`">
                  Brak danych
                </div>
                <div v-else>
                  <v-chip
                    v-for="(set, index) in stats.setsPlayed.slice(0, 5)"
                    :key="index"
                    x-small
                    class="mr-1 mb-1"
                    :style="`background: ${darkMode ? '#111a24' : '#ffffff'}; color: ${darkMode ? '#f3f6f5' : '#172422'}; border: 1px solid ${darkMode ? 'rgba(148, 163, 184, 0.2)' : 'rgba(30, 45, 42, 0.12)'};`"
                  >
                    {{ set }}
                  </v-chip>
                  <div v-if="stats.setsPlayed.length > 5" class="text-caption mt-2" :style="`color: ${darkMode ? '#9cb0aa' : '#536561'};`">
                    +{{ stats.setsPlayed.length - 5 }} więcej
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions :style="`background: ${darkMode ? '#172433' : '#f7f9f7'};`">
        <v-btn text @click="exportStats" :style="`color: ${darkMode ? '#60a5fa' : '#2563eb'}; font-weight: 700;`">
          <v-icon left small>mdi-download</v-icon>
          Eksportuj
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn text @click="dialog = false" :style="`color: ${darkMode ? '#9cb0aa' : '#536561'}; font-weight: 700;`">
          Zamknij
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { getStatistics, getCurrentStreak, exportStatistics } from '@/utils/statistics.js';

export default {
  name: 'Statistics',
  props: {
    darkMode: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    dialog: false,
    selectedPeriod: '30',
    stats: { setsPlayed: [] },
    currentStreak: 0,
    chart: null,
  }),
  watch: {
    selectedPeriod() {
      this.loadStats();
    },
    dialog(val) {
      if (val) {
        this.loadStats();
        this.$nextTick(() => {
          this.renderChart();
        });
      }
    },
  },
  methods: {
    loadStats() {
      this.stats = getStatistics(parseInt(this.selectedPeriod));
      this.currentStreak = getCurrentStreak();
    },
    renderChart() {
      const canvas = this.$refs.accuracyChart;
      if (!canvas) return;
      
      const ctx = canvas.getContext('2d');
      const width = canvas.parentElement.clientWidth;
      const height = 200;
      canvas.width = width;
      canvas.height = height;
      
      // Clear canvas
      ctx.clearRect(0, 0, width, height);
      
      const data = this.stats.dailyStats || [];
      if (data.length === 0) {
        ctx.fillStyle = this.darkMode ? '#9cb0aa' : '#536561';
        ctx.font = '14px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Brak danych', width / 2, height / 2);
        return;
      }
      
      const padding = 40;
      const chartWidth = width - padding * 2;
      const chartHeight = height - padding * 2;
      
      // Draw axes
      ctx.strokeStyle = this.darkMode ? 'rgba(148, 163, 184, 0.25)' : 'rgba(30, 45, 42, 0.15)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(padding, padding);
      ctx.lineTo(padding, height - padding);
      ctx.lineTo(width - padding, height - padding);
      ctx.stroke();
      
      // Draw data
      if (data.length > 1) {
        const stepX = chartWidth / (data.length - 1);
        
        ctx.strokeStyle = this.darkMode ? '#60a5fa' : '#2563eb';
        ctx.lineWidth = 2;
        ctx.beginPath();
        
        data.forEach((day, index) => {
          const x = padding + index * stepX;
          const accuracy = day.questions > 0 ? (day.correct / day.questions) * 100 : 0;
          const y = height - padding - (accuracy / 100) * chartHeight;
          
          if (index === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
          
          // Draw point
          ctx.fillStyle = this.darkMode ? '#60a5fa' : '#2563eb';
          ctx.beginPath();
          ctx.arc(x, y, 4, 0, Math.PI * 2);
          ctx.fill();
        });
        
        ctx.stroke();
      }
      
      // Draw labels
      ctx.fillStyle = this.darkMode ? '#9cb0aa' : '#536561';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'center';
      
      data.forEach((day, index) => {
        if (index % Math.ceil(data.length / 5) === 0) {
          const x = padding + index * (chartWidth / (data.length - 1));
          const date = new Date(day.date);
          const label = `${date.getDate()}/${date.getMonth() + 1}`;
          ctx.fillText(label, x, height - padding + 15);
        }
      });
    },
    exportStats() {
      exportStatistics();
    },
  },
};
</script>

<style scoped>
.chart-container {
  width: 100%;
}
</style>
